/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  CellType,
  ComponentType,
  IListComponentModel,
  IMediaModel,
  Orientation,
  useCanWatchAuthContent,
} from "@nf/common";
import cx from "classnames";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import type { IMediaDetailsScreenProps } from "../MediaDetailsScreen";

const GridComponent = dynamic<any>(() =>
  import(
    "components/Configuration/ListComponent/components/GridComponent"
  ).then((mod) => mod.GridComponent)
);
const MediaDetailsScreen = dynamic<IMediaDetailsScreenProps>(() =>
  import("../MediaDetailsScreen").then((mod) => mod.MediaDetailsScreen)
);
const EpisodeListItem = dynamic<any>(() =>
  import("components/EpisodeListItem").then((mod) => mod.EpisodeListItem)
);
const MediaDetailsBottomContent = dynamic<any>(() =>
  import("components/Media/MediaDetailsBottomContent").then(
    (mod) => mod.MediaDetailsBottomContent
  )
);
const SeasonSelect = dynamic<any>(
  () => import("components/SeasonSelect").then((mod) => mod.SeasonSelect),
  { ssr: false }
);

import styles from "./SeriesDetailsScreen.module.scss";

export const SeriesDetailsScreen = (props: any): JSX.Element => {
  const { t } = useTranslation("translations");
  const media = props?.media;
  const { canWatch } = useCanWatchAuthContent(media);
  const router = useRouter();
  const selectedSeason = router.query.season as string | undefined;
  const [columns, setColumns] = useState(3);

  const changeSeason = (seasonId: string | number) => {
    router.replace(
      {
        pathname: router.asPath.split("?")[0],
        query: {
          season: seasonId,
        },
      },
      undefined,
      { scroll: false, shallow: true }
    );
  };

  const handleResize = useCallback(() => {
    if (window.innerWidth < 600) {
      setColumns(1);
    } else if (window.innerWidth < 1000) {
      setColumns(2);
    } else {
      setColumns(3);
    }
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const renderSelect = () => {
    const seasons = media?.Media;
    return (
      seasons && (
        <SeasonSelect
          title={t("web_series_information_season")}
          items={seasons}
          selected={selectedSeason}
          onChange={changeSeason}
        />
      )
    );
  };

  const renderEpisodesList = () => {
    if (!media) {
      return undefined;
    }

    const episodesList: IListComponentModel = {
      ComponentTypeCode: ComponentType.List,
      CellType: CellType.Custom,
      Orientation: Orientation.Grid,
      VisibleItemsCount: 3,
      MediaList: [],
      CutomListItem: <EpisodeListItem />,
    };

    if (!selectedSeason) {
      episodesList.MediaList = media?.Media ? media.Media[0]?.Media : [];
    } else {
      const season = media.Media?.find((row: IMediaModel) => {
        return row.Id === selectedSeason;
      });

      if (season) episodesList.MediaList = season.Media || [];
    }

    return (
      <GridComponent
        noAutoColumns
        dynamicHeight
        columns={columns}
        dataLength={episodesList.MediaList ? episodesList.MediaList.length : 0}
        colPadding={0}
        rowPadding={16}
        component={episodesList}
      />
    );
  };

  return (
    <MediaDetailsScreen media={media} className={styles.seriesDetails}>
      {media && (
        <MediaDetailsBottomContent media={media}>
          <>
            {renderSelect()}
            <div
              className={cx(styles.episodesList, {
                [styles.episodesListEnableEvents]: canWatch,
              })}
            >
              {renderEpisodesList()}
            </div>
          </>
        </MediaDetailsBottomContent>
      )}
    </MediaDetailsScreen>
  );
};

export default SeriesDetailsScreen;
